import React, { useState, useEffect } from "react";
import HeaderContainer from "./Header";
import { useNavigate } from "react-router-dom";
import { env, useGlobalState } from "../helpers/GlobalStateProvider"; // Import your wallet hook
import axios from "axios";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { apiUrls, getApiUrl } from "../config";
import { useModalState } from "../helpers/ModalProvider";
import { useDisconnect, useWalletClient } from "wagmi";
import { ethers } from "ethers";
import { migrateLocalChatHistory } from "./chatbot/chatHistoryStorage";
import { setToken } from "../helpers/tokenManager";
import { Box, Card, Flex, Heading, Input, Link, Stack, Text } from "@chakra-ui/react";
import { Button } from "../components/ui/button";
import { Field } from "../components/ui/field";

const Signin: React.FC<any> = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [waitingForWallet, setWaitingForWallet] = useState(false); // New state to track waiting for connection
  const navigate = useNavigate();
  const { openConnectModal } = useConnectModal();
  const { data: walletClient } = useWalletClient();

  const { showErrorMessage, handleCloseStakeModal, showResetPasswordModal } = useModalState();

  const { refreshAccountInfo, registerWallet, setBearerToken } = useGlobalState();

  const { disconnect } = useDisconnect();

  useEffect(() => {
    // If we were waiting for a wallet and now wallet.provider is available, try runWalletLogin again
    if (waitingForWallet && walletClient) {
      runWalletLogin();
    }
  }, [waitingForWallet, walletClient]);

  const handleEmailSignIn = async () => {
    if (!email.trim() || !password.trim()) {
      showErrorMessage("Please enter your email and password.");
      return;
    }
    try {
      const response = await fetch(`${getApiUrl(env)}/v3/apiKey/email_login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email_address: email,
          password: password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const message = errorData.message || "Error signing in.";
        throw new Error(message);
      }

      const data = await response.json();
      setToken(data.bearerToken);
      setBearerToken(data.bearerToken);
      migrateLocalChatHistory();
      const newInfo = await refreshAccountInfo();
      if(newInfo.emailVerified === false) navigate("/confirm_email");
      else navigate("/dashboard");
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') console.log('email sign in error:', error)
      if(error.toString().includes('Too many'))
      showErrorMessage(error.toString());
      else {
        showErrorMessage("Invalid email or password");
      }
    }
  };

  const runWalletLogin = async () => {
    setWaitingForWallet(false); // Reset waiting state now that we're attempting the login

    try {

      if (!walletClient) {
        showErrorMessage(
          "No wallet connected. Please connect your wallet first.",
        );
        return;
      }
      const provider = new ethers.BrowserProvider(walletClient);

      const signer = await provider.getSigner();
      const message = `Sign this message to authenticate with the coincap API. ${Date.now()}`;
      const signedMessage = await signer.signMessage(message);

      // Call /wallet_login
      const response = await axios.post(
        `${getApiUrl(env)}/v3/apiKey/wallet_login`,
        {
          originalMessage: message,
          signedMessage,
        },
      );

      if (response.status !== 200) {
        const msg = response.data.message || "Error signing in with wallet.";
        showErrorMessage(msg);
        return;
      }

      const data = response.data;
      setToken(data.bearerToken);
      setBearerToken(data.bearerToken);
      migrateLocalChatHistory();
      navigate("/dashboard");
      await refreshAccountInfo();
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') console.log("Error during wallet sign in:", error);

      // User intentionally rejected the wallet signature request — stay on
      // the sign-in page with no error toast. Reset the wallet-waiting state
      // so the user can try connecting again cleanly.
      const rejectionMessage = (error?.message || "").toLowerCase();
      if (
        error?.code === 4001 ||
        error?.code === "ACTION_REJECTED" ||
        rejectionMessage.includes("user rejected") ||
        rejectionMessage.includes("user denied") ||
        rejectionMessage.includes("user cancelled")
      ) {
        setWaitingForWallet(false);
        return;
      }

      if (
        error?.response?.data?.message ===
        "No user found for this wallet address."
      ) {
        if (process.env.NODE_ENV === 'development') console.log(
          "account does not yet exist for this wallet, auto registering",
        );
        await registerWallet();
        navigate("/dashboard");
      }
      else if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        showErrorMessage(error.response.data.message);
      } else {
        showErrorMessage("An unexpected error occurred. Please try again.");
      }
    }
  };

  const handleConnectWalletClick = async () => {
    // set some big localstorage values
    if (!walletClient) {
      // No wallet connected, open the connect modal
      openConnectModal?.();
      setWaitingForWallet(true);
    } else {
      // Wallet is already connected
      runWalletLogin();
    }
  };

  useEffect(() => {
      disconnect();
  }, [])

  return (
    <Flex
      minHeight="100vh"
      width="100%"
      maxWidth="1392px"
      margin="0 auto"
      paddingTop="24px"
      paddingBottom="24px"
      direction="column"
      gap="24px"
    >
      {/* Header */}
      <HeaderContainer behavior="signin" />

      {/* Main container to center the content */}
      <Flex flex={1} justifyContent="center" alignItems="center" padding="20px">
        {/* Content wrapper for max width */}
        <Stack maxWidth="480px" width="100%" alignItems="center" gap={8}>
          {/* Page title */}
          <Heading
            as="h1"
            textAlign="center"
            color="#18181b"
            fontSize="24px"
            fontWeight="bold"
            letterSpacing="-0.4px"
            lineHeight="32px"
          >
            Sign in to your account
          </Heading>

          {/* Sign In Card */}
          <Card.Root width="100%">
            <Card.Body>
              <Stack gap={6}>
                <Stack gap={4}>
                  {/* Email field */}
                  <Field label="Email address">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      fontSize="sm"
                    />
                  </Field>

                  {/* Password field */}
                  <Stack gap="1.5" width="100%">
                    <Flex width="100%" alignItems="center" gap="10px">
                      <Text flex={1} fontSize="sm" fontWeight="medium" color="#18181b">
                        Password
                      </Text>
                      <Button
                        variant="plain"
                        onClick={showResetPasswordModal}
                        height="auto"
                        minH="0"
                        p={0}
                        fontSize="sm"
                        fontWeight="semibold"
                        color="#9333ea"
                        _hover={{ textDecoration: "underline" }}
                      >
                        Forgot password?
                      </Button>
                    </Flex>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      fontSize="sm"
                    />
                  </Stack>
                </Stack>

                {/* Sign In Button */}
                <Button
                  onClick={handleEmailSignIn}
                  variant="solid"
                  colorPalette="purple"
                  bg="#9333ea"
                  width="100%"
                >
                  Sign in
                </Button>

                {/* Connect a Wallet Section */}
                <Stack gap={6}>
                  <Flex width="100%" alignItems="center" gap={4}>
                    <Box flex={1} height="1px" bg="#e4e4e7" />
                    <Text fontSize="sm" color="#a1a1aa">
                      Or connect a wallet
                    </Text>
                    <Box flex={1} height="1px" bg="#e4e4e7" />
                  </Flex>
                  <Button
                    onClick={handleConnectWalletClick}
                    variant="outline"
                    colorPalette="purple"
                    width="100%"
                  >
                    Connect a wallet
                  </Button>
                </Stack>
              </Stack>
            </Card.Body>
          </Card.Root>

          {/* Get Started Link */}
          <Text textAlign="center" fontSize="sm" color="#a1a1aa">
            Don’t have an account?{" "}
            <Link
              href="/signup"
              rel="noopener noreferrer"
              color="#9333ea"
              fontWeight="semibold"
            >
              Get started for free
            </Link>
          </Text>
        </Stack>
      </Flex>
    </Flex>
  );
};

export default Signin;
